// landing-gate.js — the full-page landing variant of the attendee/stay gate (SA8), a true A/B
// alternate to the slide-out drawer (welcome-gate.js). Same question, same server-rendered markup
// contract (_LandingGate.cshtml) and the same SA7 stay-form wiring (popover date picker, steppers,
// the chosen-type chip swap) — but no modal: no focus trap, no open/close, no Escape/backdrop
// dismiss. This IS the page, and it leaves by navigating away rather than closing. One field of
// the stay form diverged from the drawer on 2026-09-03: a single Dates box seeded from the default
// stay, not two blank Check in/Check out buttons — see the stay-form section below.
//
// One difference in kind from welcome-gate.js, not just presentation: a completed pick ALWAYS
// navigates. There is no "already the rendered catalog — just close" case, because this partial
// only ever renders while the gate is unresolved (HomeModel.ShowLandingGate) — there is nothing
// else on screen to reveal by closing. Picking a type with no stay required navigates immediately;
// picking a type on a stay-required event reveals the stay form in place, and Find my hotels
// navigates once both dates are committed — same two-question shape as the drawer, one screen.
//
// User-directed 2026-09-10: the type cards are CHIPS here (.wl-chip, found by [data-demog-id] —
// no icon to remember, so the chosen chip keeps its server-rendered glyph), and the splash copy
// is a card of its own — beside the steps card on desktop, under it at width (2026-09-11) —
// folded to a few lines with a More/Less toggle — see the splash-fold block.

import { attachDateRangePicker, isoDate } from './modules/date-range-picker.js';
import { wireSteppers } from './modules/occupancy.js';
import { dateNodes, longDate } from './modules/spoken-date.js';

function initLandingGate() {
  const root = document.getElementById('wlGate');
  if (!root) return;

  const group = document.getElementById('wlTypes');
  const radios = Array.from(group ? group.querySelectorAll('[data-demog-id]') : []);

  const cookieName = root.dataset.cookieName || 'ebk-attendee';
  const cookiePath = root.dataset.cookiePath || '/';
  const requireStay = root.dataset.requireStay === 'true';
  const skipTypeStep = root.dataset.skipTypeStep === 'true';
  const hasStoredSelection = root.dataset.hasSelection === 'true';
  const baseUrl = root.dataset.baseUrl || '';
  const stayCookieName = root.dataset.stayCookieName || '';

  const typePanel = root.querySelector('[data-type-panel]');
  const stayPanel = root.querySelector('[data-step-panel="2"]');
  const chosen = root.querySelector('[data-chosen]');
  const chosenLbl = root.querySelector('[data-chosen-lbl]');
  const changeBtn = root.querySelector('[data-chosen-change]');

  // Which question is on screen. Starts at 'stay' when the event withholds the type prompt but
  // still requires a stay (the type panel is never rendered at all in that case — see
  // _LandingGate.cshtml) — matching welcome-gate.js's skipTypeStep handling.
  let asking = skipTypeStep ? 'stay' : 'type';
  let typeName = '';
  // Assigned by the stay block below; a no-op until (and unless) the stay form exists.
  let syncSummary = () => {};
  // 2026-10-20: moves the rooms stepper's bound to the chosen type's cap (data-max-rooms on the type
  // chip — the admin tool's "Maximum No. of Rooms"); set once the steppers are wired.
  let applyRoomCap = () => {};

  const labelOf = (btn) => btn?.querySelector('.wl-chip-lbl')?.textContent?.trim() ?? '';
  const rememberType = (btn) => {
    typeName = labelOf(btn);
  };

  // ── SA8 · which question the column asks (same in-place swap as welcome-gate.js's SA7 block,
  // minus the modal-only aria-labelledby retarget — there is no dialog here to relabel). ─────────
  const showTypes = (moveFocus = true) => {
    asking = 'type';
    if (typePanel) typePanel.hidden = false;
    if (chosen) chosen.hidden = true;
    if (stayPanel) stayPanel.hidden = true;
    syncSummary();
    if (moveFocus && group) {
      group.setAttribute('tabindex', '-1');
      group.focus({ preventScroll: true });
    }
  };

  const showStay = (moveFocus = true) => {
    asking = 'stay';
    if (typePanel) typePanel.hidden = true;
    if (chosen) {
      chosen.hidden = false;
      if (chosenLbl) chosenLbl.textContent = typeName;
    }
    if (stayPanel) stayPanel.hidden = false;
    syncSummary();
    if (moveFocus && stayPanel) {
      stayPanel.setAttribute('tabindex', '-1');
      stayPanel.focus({ preventScroll: true });
      // User-directed 2026-09-11: the form reveals BELOW the chosen-type row, and on a laptop
      // viewport its bottom half (steppers, Find my hotels) landed under the fold — the visitor
      // had to know to scroll to find the controls the click just produced. 'nearest' is the
      // whole contract: nothing moves when the form is already on screen, the page rolls up
      // just far enough when its bottom is cut off, and a form taller than the viewport pins
      // its top (the heading) rather than its bottom. .wl-left .wm-stay's scroll-margin keeps
      // the card's own padding in view under the button. Only on a click — moveFocus is false
      // for the boot-time auto-advance, where scrolling a freshly loaded page would be a jolt.
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      stayPanel.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    }
  };

  // Picking a type always commits (FB68's rule holds here too — no separate submit step) and
  // either reveals the stay question in place, or navigates straight away when there is nothing
  // else to ask.
  const chooseType = (btn) => {
    const demogId = btn.dataset.demogId;
    document.cookie =
      `${cookieName}=${encodeURIComponent(demogId)}; path=${cookiePath}; max-age=${60 * 60 * 24 * 30}; samesite=lax; secure`;

    if (requireStay) {
      rememberType(btn);
      radios.forEach((r) => {
        const on = r === btn;
        r.classList.toggle('wl-chip--on', on);
        r.querySelector('.wl-chip-ck')?.toggleAttribute('hidden', !on);
        if (on) r.setAttribute('aria-current', 'true');
        else r.removeAttribute('aria-current');
      });
      applyRoomCap(btn);
      showStay();
      return;
    }

    document.dispatchEvent(new CustomEvent('ebk:busy', { detail: { label: 'Loading your rates…' } }));
    // Reload IN PLACE (welcome-gate.js's identical branch does the same) rather than navigating to
    // the bare baseUrl — a visitor who arrived with an explicit stay already on the URL (a
    // bookmarked/shared link) must not lose it just because they picked their attendee type here.
    window.location.reload();
  };

  radios.forEach((btn) => btn.addEventListener('click', () => chooseType(btn)));
  // Change brings the cards back and forgets nothing: the stay form is only hidden, so any dates
  // or occupancy already entered survive — same contract as the drawer's Change.
  changeBtn?.addEventListener('click', () => showTypes());

  // ── the splash fold (user-directed 2026-09-10) ──────────────────────────────────────────────
  // _LandingGate.cshtml renders the splash already clamped (.wl-splash--clamp) with the More
  // button hidden. Whether there is anything under the fold is only knowable once laid out, so it
  // is measured here: copy that fits unclamps itself and never shows a link; copy that overflows
  // gets the More/Less toggle. Wired BEFORE the stay form on purpose — this is the only thing that
  // can ever unfold server-clamped copy, so it must not sit downstream of the picker/stepper
  // wiring where a bad date attribute could throw first and leave an announcement truncated.
  // Open/closed has ONE source of truth, the button's aria-expanded; the class and the label are
  // derived from it in setOpen(), so no path can move one without the others.
  const splash = root.querySelector('[data-splash]');
  if (splash) {
    const body = splash.querySelector('[data-splash-body]');
    const moreBtn = splash.querySelector('[data-splash-more]');
    const moreLbl = splash.querySelector('[data-splash-more-lbl]');
    const isOpen = () => moreBtn?.getAttribute('aria-expanded') === 'true';
    const setOpen = (open) => {
      splash.classList.toggle('wl-splash--clamp', !open);
      // The grown fold is an inline max-height (see measure); the class alone cannot lift it.
      if (open) body.style.maxHeight = '';
      moreBtn?.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (moreLbl) moreLbl.textContent = open ? 'Less' : 'More';
    };

    // Only ever measures while FOLDED (an unfolded read is never refolded under the reader).
    // Starts from the stylesheet's five-line fold every time — the inline grow from a previous
    // pass is cleared first — so the base is always the CSS figure, never a stale growth.
    //
    // The grow (user-directed 2026-09-11, a tall-screen screenshot with bare canvas under the
    // footer): on this page <main> is flex:1 in a viewport-tall body (body.wl-body), so the room
    // the content is not using is main's height less the page's; add to that whatever the steps
    // card stands taller than this card (its column would be blank there anyway), and raise the
    // fold by the sum. Five lines is the floor; a viewport with room reads more announcement
    // instead of more ground. Copy that then fits entirely unclamps and hides More, exactly as
    // short copy always has.
    //
    // The third term (user-directed 2026-10-15, a 1920×890 screenshot where the fold sat at five
    // lines with the whole footer on screen under it): once the slack is used up, every further
    // pixel pushes the footer down by a pixel, and the user set the limit at the footer's
    // "Expovision is the Official Housing Provider for …" line (.footer-trust-lede) — the fold may
    // grow until THAT line's bottom edge reaches the bottom of the viewport, and no further. The
    // note under it may leave the first screen; the provider line may not. Measured as document
    // offsets (rect + scrollY) so a page already scrolled when this runs (bfcache restore, anchor)
    // reads the same figure as a fresh load. Pages without the notice (no footer model) keep the
    // slack-only behaviour.
    const left = root.querySelector('.wl-left');
    const main = document.getElementById('main');
    const lede = document.querySelector('.footer-trust-lede');
    const measure = () => {
      if (!body || !moreBtn || isOpen()) return;
      body.style.maxHeight = '';
      splash.classList.add('wl-splash--clamp');
      const base = body.clientHeight;
      if (body.scrollHeight <= base + 1) {
        splash.classList.remove('wl-splash--clamp');
        moreBtn.hidden = true;
        return;
      }
      let growth = 0;
      let under = 0;
      if (main) {
        const free = main.getBoundingClientRect().height - root.getBoundingClientRect().height;
        if (free > 0) growth += free;
      }
      if (left) {
        under = left.getBoundingClientRect().bottom - splash.getBoundingClientRect().bottom;
        if (under > 0) growth += under;
      }
      if (growth > 0) body.style.maxHeight = `${Math.floor(base + growth)}px`;
      if (lede) {
        // Read AFTER the slack is spent, not predicted from it: the first pass's "free" figure
        // can over-read (the banner <img> carries no size attributes, so before it loads the
        // page is ~100px shorter and main stretches by that much), and the only figure that
        // matters is where the provider line actually landed. Whatever room is left between it
        // and the bottom of the viewport is the fold's to take; a negative figure means the
        // first pass over-reached and is given back — never below the five-line floor plus the
        // "under" share, since that share moves nothing and giving it back would hide lines
        // without lifting the footer.
        const residual = window.innerHeight - (lede.getBoundingClientRect().bottom + window.scrollY);
        const floor = base + Math.max(0, under);
        const target = Math.max(floor, body.clientHeight + residual);
        body.style.maxHeight = target > base + 1 ? `${Math.floor(target)}px` : '';
      }
      const overflows = body.scrollHeight > body.clientHeight + 1;
      if (!overflows) {
        splash.classList.remove('wl-splash--clamp');
        body.style.maxHeight = '';
      }
      moreBtn.hidden = !overflows;
    };

    moreBtn?.addEventListener('click', () => {
      const open = !isOpen();
      setOpen(open);
      // Folding back re-fits the fold to the room on screen, then brings the start of the copy
      // back into view so "Less" does not land on a blank stretch of card.
      if (!open) {
        measure();
        if (body.getBoundingClientRect().top < 0) body.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    });
    // Clipped is not hidden: a link under the fold is still in the tab order (the sanitizer keeps
    // <a>), and focusing it would scroll the clipped box under the fade with no ring to see.
    // Unfold instead, so keyboard focus always lands on visible text.
    body?.addEventListener('focusin', () => {
      if (!isOpen() && !moreBtn.hidden) setOpen(true);
    });

    measure();
    // The banner image sizes itself only once it loads (no width/height attributes), which moves
    // the footer by its height after DOMContentLoaded; the fold is re-fitted once everything has
    // a size. measure() is a no-op once the reader has unfolded, so this cannot refold under them.
    if (document.readyState !== 'complete') window.addEventListener('load', measure, { once: true });
    // Re-measure on a WIDTH change only (a narrower column pushes copy under a fixed-height fold).
    // The observer watches the box whose height measure() itself toggles, so it is width-guarded
    // to stay off its own feedback; the instance is held (nightly-matrix.js's lesson) so it is
    // not collected. The width is the observer's OWN content-box figure — observe() fires once
    // immediately, and that first entry seeds it; seeding from getBoundingClientRect (border-box,
    // so never equal to contentRect) had let that first entry through as a phantom change and
    // measured a third time at startup (2026-09-11 review). Web fonts swap after DOMContentLoaded
    // and change the line count without changing the clamped outer box, so that is re-measured
    // explicitly.
    let lastWidth = null;
    if (typeof ResizeObserver === 'function') {
      const splashObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width === undefined || width === lastWidth) return;
        const seeding = lastWidth === null;
        lastWidth = width;
        if (!seeding) measure();
      });
      splashObserver.observe(splash);
      // The steps card changing height (type picked → stay form revealed, and back on Change)
      // changes how much this card can grow beside it. Its size never depends on this card's
      // (align-items:start), so this cannot feed itself; the first, immediate entry is skipped
      // for the same reason as above.
      if (left) {
        let leftSeeded = false;
        const leftObserver = new ResizeObserver(() => {
          if (!leftSeeded) { leftSeeded = true; return; }
          measure();
        });
        leftObserver.observe(left);
      }
    }
    // Viewport HEIGHT is the other input to the grow, and no element observer sees it.
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure);
  }

  // ── SA8 · the stay form (steppers/summary wiring shared with welcome-gate.js's SA7 block; the
  // date field itself diverged 2026-09-03 — one box seeded from the default stay, not two blank
  // Check in/Check out buttons — see the markup note in _LandingGate.cshtml). ───────────────────
  if (requireStay && stayPanel) {
    const pop = stayPanel.querySelector('[data-stay-pop]');
    const dateBtn = stayPanel.querySelector('[data-date-open]');
    const dateValEl = stayPanel.querySelector('[data-date-val]');
    const nightsEl = stayPanel.querySelector('[data-date-nights]');
    const goBtn = stayPanel.querySelector('[data-stay-go]');

    // User-directed 2026-09-03: seeded from the default stay, not blank — _LandingGate.cshtml
    // renders the button's own label from the same default and sets the popover's data-checkin/
    // data-checkout to match, so this mirrors what is already on screen rather than re-deriving it.
    let picked = { checkin: null, checkout: null };
    let occ = { rooms: 1, guests: 1 };
    const FMT = { weekday: 'short', month: 'short', day: 'numeric' };

    // Light popover plumbing — no focus trap to coordinate with (unlike the drawer, there is no
    // outer trap here at all): open/close, outside-click, Escape, aria-expanded on the field, and
    // focus handed back to it on close.
    const gatePopover = () => {
      let current = null; // { pop, btn }
      const close = () => {
        if (!current) return;
        const { pop: p, btn } = current;
        current = null;
        const hadFocus = p.contains(document.activeElement) || document.activeElement === document.body;
        p.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        if (hadFocus) btn.focus();
      };
      const open = (p, btn, { onOpen } = {}) => {
        const wasOpen = current?.pop === p;
        close();
        if (wasOpen) return;
        p.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        onOpen?.();
        current = { pop: p, btn };
        p.querySelector('[data-dp-input]')?.focus();
      };
      document.addEventListener('click', (e) => {
        if (!current) return;
        const path = e.composedPath();
        if (!path.includes(current.pop) && !path.includes(current.btn)) close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && current) close();
      });
      return { open, close, isOpen: (p) => current?.pop === p };
    };
    const popovers = gatePopover();

    // "Thu, Sep 3 - Fri, Sep 4" plus a nights pill (user-directed 2026-09-03) — one box, not the
    // Check in/Check out pair. Both elements only ever show real dates now (the box starts on the
    // default stay, never a "Select date" placeholder), so there is no blank/--set toggle to track.
    const syncFields = () => {
      const { checkin, checkout } = picked;
      if (!checkin || !checkout) return;
      if (dateValEl) {
        // Shown short, heard in full ("Thursday, September 3 to Friday, September 4"; ADA 2026-10-22).
        dateValEl.replaceChildren(...dateNodes(checkin, {
          shown: `${checkin.toLocaleDateString('en-US', FMT)} - ${checkout.toLocaleDateString('en-US', FMT)}`,
          spoken: `${longDate(checkin)} to ${longDate(checkout)}`,
        }));
      }
      if (nightsEl) {
        const n = Math.round((checkout - checkin) / 86400000);
        nightsEl.textContent = `${n} night${n === 1 ? '' : 's'}`;
      }
    };

    syncSummary = () => {
      if (goBtn) goBtn.disabled = !(asking === 'stay' && picked.checkin && picked.checkout);
    };

    const picker = attachDateRangePicker({
      button: dateBtn,
      pop,
      config: pop.dataset,
      popovers,
      // Apply commits the dates HERE; nothing navigates until Find my hotels.
      applyNavigates: false,
      onApply: (ci, co) => {
        picked = { checkin: ci, checkout: co };
        pop.dataset.checkin = isoDate(ci);
        pop.dataset.checkout = isoDate(co);
        syncFields();
        syncSummary();
        popovers.close();
      },
    });
    // The default stay _LandingGate.cshtml already rendered into the button's label and the
    // popover's data-checkin/data-checkout — mirror it into `picked` so Find my hotels is enabled
    // and the two agree from first paint, with no click required.
    picked = { checkin: picker.defaults.checkin, checkout: picker.defaults.checkout };

    const steppers = wireSteppers(stayPanel, occ, (state) => {
      occ = state;
      syncSummary();
    });
    const roomRow = stayPanel.querySelector('[data-step-key="rooms"]');
    applyRoomCap = (btn) => {
      const cap = +btn?.dataset.maxRooms;
      if (!roomRow || !(cap > 0)) return;
      roomRow.dataset.max = String(cap);
      steppers.sync(); // re-clamps rooms/guests under the new bound and re-disables "+"
    };

    goBtn?.addEventListener('click', (e) => {
      const { checkin, checkout } = picked;
      if (asking !== 'stay' || !checkin || !checkout) return;
      if (stayCookieName) {
        const value = `${isoDate(checkin)}~${isoDate(checkout)}~${occ.rooms}~${occ.guests}`;
        document.cookie =
          `${stayCookieName}=${encodeURIComponent(value)}; path=${cookiePath}; ` +
          `max-age=${60 * 60 * 24 * 30}; samesite=lax; secure`;
      }
      document.dispatchEvent(new CustomEvent('ebk:busy',
        { detail: { label: 'Finding your hotels…', origin: e.currentTarget } }));
      window.location.href =
        `${baseUrl}?ci=${isoDate(checkin)}&co=${isoDate(checkout)}&rooms=${occ.rooms}&guests=${occ.guests}`;
    });

    syncFields();
    syncSummary();
  }

  // First paint: a returning visitor who already answered the type question must land on the
  // stay form directly, not be asked again — same contract as welcome-gate.js's typeAnswered()
  // + boot-time auto-advance (welcome-gate.js's final block). Skipped when skipTypeStep is true:
  // _LandingGate.cshtml already server-renders the stay panel open in that case, and there is no
  // type chip to remember a label from.
  if (requireStay && !skipTypeStep && hasStoredSelection) {
    const preselected = radios.find((r) => r.classList.contains('wl-chip--on'));
    if (preselected) {
      rememberType(preselected);
      showStay(false);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLandingGate);
} else {
  initLandingGate();
}
